import React, { useState } from "react";
import { useIntl } from "react-intl";
import { useHabitContext } from "../../context/HabitContext";
import { useUserPreferences } from "../../hooks/useUserPreferences";
import {
  getMonthName,
} from "../../utils/dateUtils";
import SectionHeader from "../common/SectionHeader";
import { sanitizeHtml } from "../../utils/sanitizeHtml";
import Spinner from "../common/Spinner";
import "./NotesListView.scss";

const NotesListView: React.FC = () => {
  const { formatMessage: t, formatDate: intlFormatDate } = useIntl();
  const { currentMonth, currentYear, isLoading } = useHabitContext();
  const { settings } = useUserPreferences();

  const [viewMonth, setViewMonth] = useState(currentMonth);
  const [viewYear, setViewYear] = useState(currentYear);

  const navigateMonth = (delta: number) => {
    setViewMonth(prev => {
      const next = prev + delta;
      if (next < 0) {
        setViewYear(y => y - 1);
        return 11;
      }
      if (next > 11) {
        setViewYear(y => y + 1);
        return 0;
      }
      return next;
    });
  };

  const prefix = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-`;
  const noteRows = Object.entries(settings.dailyNotes ?? {})
    .filter(([key, text]) => key.startsWith(prefix) && text?.trim())
    .map(([key, text]) => ({ day: Number(key.slice(-2)), text }))
    .sort((a, b) => a.day - b.day);

  const formatNoteDate = (day: number) => {
    return intlFormatDate(new Date(viewYear, viewMonth, day), { day: 'numeric', month: 'short' });
  };

  return (
    <div className="notes-list-view">
      <SectionHeader
        title={`${getMonthName(viewMonth)} ${viewYear}`}
        onPrev={() => navigateMonth(-1)}
        onNext={() => navigateMonth(1)}
        prevLabel={t({ id: 'stats.notes.previousMonth' })}
        nextLabel={t({ id: 'stats.notes.nextMonth' })}
      />

      {isLoading ? (
        <Spinner block />
      ) : noteRows.length === 0 ? (
        <p className="notes-list-view__empty">
          {t({ id: 'year.notes.noNotes' })}
        </p>
      ) : (
        <div className="notes-list-view__table-wrap">
          <table className="notes-list-view__table">
            <thead>
              <tr>
                <th className="notes-list-view__th notes-list-view__th--date">{t({ id: 'year.notes.dateColumn' })}</th>
                <th className="notes-list-view__th notes-list-view__th--note">{t({ id: 'year.notes.noteColumn' })}</th>
              </tr>
            </thead>
            <tbody>
              {noteRows.map((row) => (
                <tr key={row.day} className="notes-list-view__tr">
                  <td className="notes-list-view__td notes-list-view__td--date">
                    {formatNoteDate(row.day)}
                  </td>
                  <td
                    className="notes-list-view__td notes-list-view__td--note"
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(row.text),
                    }}
                  />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default NotesListView;
